import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimePicker } from 'shared/components/ui/time-picker';
import { Toggle } from 'shared/components/ui/toggle';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './habit-form.style';

export type ScheduleTimeRangeValue = {
  startTime: number | null;
  duration: number | null;
};

export type ScheduleTimeRangeInputProps = {
  enabled: boolean;
  onToggleEnabled: () => void;
  startTime?: number | null;
  duration?: number | null;
  timeFormat: TimeFormatOptions;
  onChange: (value: ScheduleTimeRangeValue) => void;
};

export const ScheduleTimeRangeInput: React.FC<ScheduleTimeRangeInputProps> = ({
  enabled,
  onToggleEnabled,
  startTime,
  duration,
  timeFormat,
  onChange,
}) => {
  const { t } = useTranslation();

  const endTime =
    isNumber(startTime) && isNumber(duration)
      ? startTime + duration
      : undefined;

  const placeholder = t(
    timeFormat === TimeFormatOptions.Military
      ? 'forms.habit.fields.reminder-time.military.placeholder'
      : 'forms.habit.fields.reminder-time.standard.placeholder',
  );

  // The two fields are start + end times; duration is their difference. Moving
  // the start keeps the end fixed (recomputes duration); setting the end
  // requires a start to compute against.
  const onChangeStart = (time?: number) => {
    if (!isNumber(time)) {
      onChange({ startTime: null, duration: null });
      return;
    }

    if (isNumber(endTime)) {
      onChange({ startTime: time, duration: Math.max(endTime - time, 0) });
      return;
    }

    onChange({ startTime: time, duration: duration ?? null });
  };

  const onChangeEnd = (time?: number) => {
    if (!isNumber(time) || !isNumber(startTime)) {
      onChange({ startTime: startTime ?? null, duration: null });
      return;
    }

    onChange({ startTime, duration: Math.max(time - startTime, 0) });
  };

  return (
    <Styled.ToggleRow>
      <Toggle
        value="scheduleTime"
        checked={enabled}
        onChange={onToggleEnabled}
      />
      {enabled && (
        <Styled.TimeFields>
          <TimePicker
            value={startTime}
            timeFormat={timeFormat}
            onChange={onChangeStart}
            placeholder={placeholder}
            showAsFormInput
          />
          <TimePicker
            value={endTime}
            timeFormat={timeFormat}
            onChange={onChangeEnd}
            placeholder={placeholder}
            showAsFormInput
          />
        </Styled.TimeFields>
      )}
    </Styled.ToggleRow>
  );
};
